import { Injectable, OnDestroy } from '@angular/core'
import { WorkspaceService } from '@app/modules/workspaces/services/workspace.service'
import { PosthogService } from '@app/services/posthog.service'
import { ReplaySubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { UserRoles } from '../enums/user-roles.enum'
import { AuthService } from './auth.service'

const ROLES_CAN_ADD = {
  accountant: [],
  viewer: [],
  approver: [],
  collaborator: [UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  admin: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  owner: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT]
}
const ROLES_CAN_DELETE = {
  accountant: [],
  viewer: [],
  approver: [],
  collaborator: [UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  admin: [UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  owner: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT]
}

const ROLES_CAN_CHANGE = {
  accountant: [],
  viewer: [],
  approver: [],
  collaborator: [UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  admin: [UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT],
  owner: [UserRoles.ADMIN, UserRoles.COLLABORATOR, UserRoles.APPROVER, UserRoles.VIEWER, UserRoles.ACCOUNTANT]
}
@Injectable({
  providedIn: 'root'
})
export class WorkspaceRoleService implements OnDestroy {
  private _currentUserId: number
  private _role: UserRoles = UserRoles.APPROVER
  private _role$: ReplaySubject<string> = new ReplaySubject(1)
  private _destroy$: Subject<void> = new Subject()

  constructor(private auth: AuthService, private workspace: WorkspaceService, private _posthogService: PosthogService) {
    this._currentUserId = this.auth.user?.id ?? 0
    this.auth.userChange.pipe(takeUntil(this._destroy$)).subscribe((user) => {
      if (!user) return
      this._currentUserId = user?.id
      this.updateUserRole()
    })
    this.auth.currentWorkspaceChange.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.updateUserRole()
    })
  }

  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Checks if user is superadmin
   * @returns boolean
   */
  isSuperAdmin(): boolean {
    return this.auth.isAdmin
  }

  /**
   * Gets the current user role on the workspace
   * @returns Subject<string>
   */
  getUserRole(): Subject<string> {
    return this._role$
  }

  /**
   * Updates the user role based on the current workspace.
   * Runs synchronously so route guards see the role for the selected workspace immediately.
   */
  private updateUserRole() {
    if (this.isSuperAdmin()) {
      this._role = UserRoles.ADMIN
      this._role$.next(this._role)
      this._posthogService.setWorkspace(this.auth.currentWorkspace, 'Super User')
      return
    }

    const workspace = this.auth.currentWorkspace
    if (!workspace?.id) return

    const users = workspace.workspace_users
    const user = users?.find((workspaceUser) => workspaceUser?.user_id === this._currentUserId) ?? users?.find((workspaceUser) => workspaceUser?.user?.id === this._currentUserId)

    this._role = user?.is_owner ? 'owner' : user?.role || null
    this._role$.next(this._role)

    if (this.auth.isAdmin) {
      this.auth.userRoleInCurrentWorksapce.next('admin')
    } else if (user) {
      this.auth.userRoleInCurrentWorksapce.next(user.role || (user.is_owner ? 'owner' : null))
    } else {
      this.auth.userRoleInCurrentWorksapce.next(null)
    }

    this._posthogService.setWorkspace(workspace, this._role)
  }

  /**
   * Recompute the current workspace role from workspace_users (synchronous).
   * Call before role-based navigation decisions after switching organizations.
   */
  public syncRoleFromCurrentWorkspace(): void {
    this.updateUserRole()
  }
  /**
   * Return the current user role based on the provided workspace users list
   * @param workspaceUsers any[]
   * @returns string
   */
  getUserRoleFromWorkspaceUsers(workspaceUsers: any[]): string {
    if (this.isSuperAdmin()) return UserRoles.ADMIN
    const user = workspaceUsers.find((user) => user.user_id === this._currentUserId)
    return user.is_owner ? 'owner' : user.role
  }

  /**
   * Checks if user can edit an event
   * @returns boolean
   */
  canEditEvent(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER, UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can create an event
   * @returns boolean
   */
  canCreateEvent(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER, UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can add a user to a workspace
   * @returns boolean
   */
  canAddUsers(role: string = this._role): boolean {
    return ['owner', UserRoles.ADMIN, UserRoles.COLLABORATOR].includes(role)
  }

  /**
   * Checks if user can access webhooks.
   * Requires a confirmed email, then owner/admin/superadmin.
   * @returns boolean
   */
  public canAccessWebhooks(): boolean {
    if (!this.auth.user?.confirmed) return false
    if (this.isSuperAdmin()) return true
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * Checks if user can access API keys.
   * Requires a confirmed email, then owner/admin/superadmin.
   * @returns boolean
   */
  public canAccessApiKeys(): boolean {
    if (!this.auth.user?.confirmed) return false
    if (this.isSuperAdmin()) return true
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * List of roles that the user can assign to other users
   * @param role Current user role
   * @returns boolean
   */
  rolesListToAdd(role: string = this._role): string[] {
    return ROLES_CAN_ADD[role]
  }

  /**
   * List of roles that the user can assign to other users
   * @param role Current user role
   * @returns boolean
   */
  rolesListToDelete(role: string = this._role): string[] {
    return ROLES_CAN_DELETE[role]
  }

  /**
   * List of roles that the user can change for other users
   * @param role Current user role
   * @returns boolean
   */
  rolesListToChange(role: string = this._role): string[] {
    return ROLES_CAN_CHANGE[role]
  }

  /**
   * Checks if user can view the database
   * @returns boolean
   */
  canViewDatabase(): boolean {
    return ![UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can edit the database
   * @returns boolean
   */
  canEditDatabase(): boolean {
    return ![UserRoles.ACCOUNTANT].includes(this._role)
  }

  /**
   * Checks if user can export an event's database
   * @returns boolean
   */
  canExportDatabase(): boolean {
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * Checks if user can import to an event's database
   * @returns boolean
   */
  canImportToEvent(): boolean {
    return ['owner', UserRoles.ADMIN, UserRoles.COLLABORATOR].includes(this._role)
  }

  /**
   * Checks if user can export master database
   * @returns boolean
   */
  canExportMasterDatabase(): boolean {
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * Checks if user can add creditcard
   * @returns boolean
   */
  canAddCreditcard(): boolean {
    return this.canEditBilling()
  }

  /**
   * Checks if user can remove creditcard
   * @returns boolean
   */
  canRemoveCreditcard(): boolean {
    return this.canEditBilling()
  }

  /**
   * Checks if user can upgrade workspace
   * @returns boolean
   */
  canUpgradeWorkspace(): boolean {
    return this.canEditBilling() && !this.auth.user?.is_admin
  }

  /**
   * Checks if user can manage the subscription / plan (customer portal).
   * @returns boolean
   */
  canEditSubscription(): boolean {
    return this.canEditBilling()
  }

  /**
   * Whether the user can edit billing (add/remove payment cards, manage plan).
   * Collaborators can access billing but cannot edit.
   * @returns {boolean} True for accountant, owner, admin, or superadmin
   */
  canEditBilling(): boolean {
    if (this.isSuperAdmin()) return true

    return ['owner', UserRoles.ADMIN, UserRoles.ACCOUNTANT].includes(this._role as string)
  }

  /**
   * Whether the user can see the billing section and top up the wallet.
   * Includes collaborators (view + top-up only).
   * @returns {boolean} True for collaborator, accountant, owner, admin, or superadmin
   */
  canAccessBilling(): boolean {
    if (this.isSuperAdmin()) return true

    return ['owner', UserRoles.ADMIN, UserRoles.ACCOUNTANT, UserRoles.COLLABORATOR].includes(this._role as string)
  }

  /**
   * Whether the current workspace role is accountant (billing-only org settings access).
   * Superadmins always return false so they keep full org-settings access.
   * @returns {boolean} True when the user is an accountant and not a superadmin
   */
  isAccountant(): boolean {
    if (this.isSuperAdmin()) return false

    return this._role === UserRoles.ACCOUNTANT
  }

  /**
   * Checks if user can change approval status
   * @returns boolean
   */
  canChangeApprovalStatus(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can see the actions column in the database
   * @returns boolean
   */
  canSeeActionsColumn(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can share events/personas in the timeline
   * @returns boolean
   */
  canShareInTimeline(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can access workspace settings
   * @returns boolean
   */
  canAccessWorkspaceSettings(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER].includes(this._role)
  }

  /**
   * Checks if user can access workspace members list
   * @returns boolean
   */
  canAccessWorkspaceMembers(): boolean {
    return ![UserRoles.VIEWER, UserRoles.APPROVER].includes(this._role)
  }

  /**
   * Checks if user can access bulk actions (for selecting rows)
   * @returns boolean
   */
  canAccessBulkActions(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can access flags menu
   * @returns boolean
   */
  canAccessFlagsMenu(): boolean {
    return true // All roles can access flags menu
  }
  //  * Checks if user can edit registration limits
  //  * @returns boolean
  //  */
  canEditRegistrationLimit(): boolean {
    return ![UserRoles.VIEWER].includes(this._role)
  }

  /**
   * Checks if user can delete archived events
   * @returns boolean
   */
  canDeleteArchivedEvents(): boolean {
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }

  /**
   * Checks if user can archive items
   * @returns boolean
   */
  canArchiveItems(): boolean {
    if (this.isSuperAdmin()) return true
    return ['owner', UserRoles.ADMIN].includes(this._role)
  }
}
